import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye, Download, Send } from "lucide-react";
import { useState } from "react";
import ContractPopup from "@/components/orders/ContractPopup";
import { useIsMobile, useIsExtraSmall } from "@/hooks/use-mobile";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";

import { ExtendedContract } from "@/types/payment";
import { formatDate } from "@/utils/dateUtils";
import { useFile } from "@/hooks/useFile";
import { useMedia } from "@/hooks/useMedia";
import { ContractService } from "@/services/contract/contractService";
import { useLanguage } from "@/context/LanguageContext";
import { ordersLabels } from "@/i18n";

export const ContractList = ({
  contracts,
  onViewContract,
}: {
  contracts: ExtendedContract[];
  onViewContract?: (contract: ExtendedContract) => void;
}) => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const ae = o.adminEconomy as Record<string, string>;

  const { downloadFile } = useFile();
  const { getDownloadUrlAsync } = useMedia();
  const [sortBy, setSortBy] = useState<string>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedContract, setSelectedContract] =
    useState<ExtendedContract | null>(null);
  const [isContractPopupOpen, setIsContractPopupOpen] = useState(false);
  const isMobile = useIsMobile();
  const isExtraSmall = useIsExtraSmall();

  // Sort the contracts results
  contracts.sort((a, b) => {
    let aVal: unknown, bVal: unknown;

    if (sortBy === "date") {
      aVal = a.contract.created_at
        ? new Date(a.contract.created_at).getTime()
        : 0;
      bVal = b.contract.created_at
        ? new Date(b.contract.created_at).getTime()
        : 0;
    } else if (sortBy === "contract_number") {
      aVal = a.contract.contract_number;
      bVal = b.contract.contract_number;
    } else if (sortBy === "regNumber") {
      aVal = a.listing.regNumber;
      bVal = b.listing.regNumber;
    } else if (sortBy === "status") {
      aVal = a.listing.status;
      bVal = b.listing.status;
    } else {
      aVal = a.contract.id;
      bVal = b.contract.id;
    }

    if (sortDir === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "signed":
        return (
          <Badge className="bg-green-100 text-green-800">
            {ae.contractStatusSigned}
          </Badge>
        );
      case "requested":
        return (
          <Badge className="bg-amber-100 text-amber-800">
            {ae.contractStatusRequested ?? "Signering begärd"}
          </Badge>
        );
      case "completed":
        return (
          <Badge className="bg-blue-100 text-blue-800">
            {ae.contractStatusCompleted}
          </Badge>
        );
      case "cancelled":
        return (
          <Badge className="bg-red-100 text-red-800">
            {ae.contractStatusCancelled}
          </Badge>
        );
      case "not_requested":
      case "draft":
      default:
        return (
          <Badge className="bg-gray-100 text-gray-800">
            {ae.contractStatusDraft ?? "Ej begärd"}
          </Badge>
        );
    }
  };

  if (isMobile || isExtraSmall) {
    return (
      <div className="space-y-4">
        {contracts.map((contract) => (
          <div
            key={contract.contract.id}
            className="border rounded-lg p-4 space-y-3"
          >
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-lg">
                  {contract.contract.contract_number}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {contract.listing.regNumber}
                </p>
              </div>
              {getStatusBadge(contract.contract.buyer_signature_status ?? "not_requested")}
            </div>

            <div className="text-sm text-muted-foreground">
              {ae.created}: {formatDate(contract.contract.created_at)}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedContract(contract);
                  setIsContractPopupOpen(true);
                }}
                className="flex-1"
              >
                <Eye className="h-4 w-4 mr-2" />
                {ae.view}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (contract.contract.contract_file) {
                    try {
                      // If mediaId exists, fetch presigned URL on-demand
                      let downloadUrl = contract.contract.contract_file.url;
                      if (
                        contract.contract.contract_file.mediaId &&
                        !contract.contract.contract_file.url
                      ) {
                        downloadUrl = await getDownloadUrlAsync({
                          mediaId: contract.contract.contract_file.mediaId,
                          expiresIn: 3600,
                        });
                      }
                      const filename = await downloadFile(
                        downloadUrl,
                        contract.contract.contract_file.name,
                      );
                      toast.success(o.invoicePopup.downloadStarted, {
                        description: o.invoicePopup.downloadDescription.replace(
                          "{filename}",
                          filename,
                        ),
                      });
                    } catch (error) {
                      const errorMessage =
                        error instanceof Error
                          ? error.message
                          : o.invoicePopup.unknownError;
                      toast.error(o.invoicePopup.downloadError, { description: `${contract.contract.contract_file.name}\n${o.invoicePopup.errorLabel}: ${errorMessage}` });
                    }
                  }
                }}
                className="flex-1"
              >
                <Download className="h-4 w-4 mr-2" />
                {ae.download}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await ContractService.sendContract(contract.contract.id);
                    toast.success(o.toasts.contractSent, { description: o.toasts.contractSentDescription });
                  } catch (error) {
                    toast.error(o.toasts.contractSendError, {
                      description: error instanceof Error
                          ? error.message
                          : o.invoicePopup.unknownError,
                    });
                  }
                }}
                className="flex-1"
              >
                <Send className="h-4 w-4 mr-2" />
                {ae.send}
              </Button>
            </div>
          </div>
        ))}
        {contracts.length === 0 && (
          <div className="text-center text-muted-foreground py-8">
            {ae.noContractsFound}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => {
                if (sortBy === "id") {
                  setSortDir(sortDir === "asc" ? "desc" : "asc");
                } else {
                  setSortBy("id");
                  setSortDir("asc");
                }
              }}
            >
              {ae.id} {sortBy === "id" && (sortDir === "asc" ? "↑" : "↓")}
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => {
                if (sortBy === "date") {
                  setSortDir(sortDir === "asc" ? "desc" : "asc");
                } else {
                  setSortBy("date");
                  setSortDir("desc");
                }
              }}
            >
              {ae.date} {sortBy === "date" && (sortDir === "asc" ? "↑" : "↓")}
            </TableHead>
            <TableHead
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => {
                if (sortBy === "regNumber") {
                  setSortDir(sortDir === "asc" ? "desc" : "asc");
                } else {
                  setSortBy("regNumber");
                  setSortDir("asc");
                }
              }}
            >
              {ae.regNo}{" "}
              {sortBy === "regNumber" && (sortDir === "asc" ? "↑" : "↓")}
            </TableHead>
            <TableHead>{ae.status}</TableHead>
            <TableHead>{ae.actions}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contracts.map((contract) => (
            <TableRow key={contract.contract.id}>
              <TableCell className="font-medium">
                {contract.contract.contract_number}
              </TableCell>
              <TableCell>{formatDate(contract.contract.created_at)}</TableCell>
              <TableCell>{contract.listing.regNumber}</TableCell>
              <TableCell>{getStatusBadge(contract.contract.buyer_signature_status ?? "not_requested")}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedContract(contract);
                      setIsContractPopupOpen(true);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (contract.contract.contract_file) {
                        try {
                          const filename = await downloadFile(
                            contract.contract.contract_file.url,
                            contract.contract.contract_file.name,
                          );
                          toast.success(o.invoicePopup.downloadStarted, {
                            description: o.invoicePopup.downloadDescription.replace(
                                "{filename}",
                                filename,
                              ),
                          });
                        } catch (error) {
                          const errorMessage =
                            error instanceof Error
                              ? error.message
                              : o.invoicePopup.unknownError;
                          toast.error(o.invoicePopup.downloadError, { description: `${contract.contract.contract_file.name}\n${o.invoicePopup.errorLabel}: ${errorMessage}` });
                        }
                      }
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        await ContractService.sendContract(
                          contract.contract.id,
                        );
                        toast.success(o.toasts.contractSent, { description: o.toasts.contractSentDescription });
                      } catch (error) {
                        toast.error(o.toasts.contractSendError, {
                          description: error instanceof Error
                              ? error.message
                              : o.invoicePopup.unknownError,
                        });
                      }
                    }}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {contracts.length === 0 && (
        <div className="text-center text-muted-foreground py-8">
          {ae.noContractsFound}
        </div>
      )}

      {/* Contract Popup */}
      {selectedContract && (
        <ContractPopup
          contract={selectedContract}
          isAdmin
          onClose={() => {
            setIsContractPopupOpen(false);
            setSelectedContract(null);
          }}
        />
      )}
    </div>
  );
};
